<template>
  <div class="gauge" :style="{ '--stroke': stroke }">
    <span v-if="label" class="gauge-label">{{ label }}</span>

    <svg :width="size" :height="size * 0.78" viewBox="0 0 100 78" class="gauge-svg" role="img" :aria-label="ariaLabel">
      <!-- frame -->
      <rect x="10" y="8" width="80" height="60" fill="none" stroke="rgba(224,224,224,0.08)" stroke-width="1" />

      <!-- track -->
      <path :d="bgArc" fill="none" stroke="rgba(255,255,255,0.06)" stroke-width="6" stroke-linecap="square" />

      <!-- threshold zone -->
      <path
        v-if="thresholdArc"
        :d="thresholdArc"
        fill="none"
        stroke="rgba(255,0,0,0.12)"
        stroke-width="6"
        stroke-linecap="square"
      />

      <!-- value -->
      <path :d="valueArc" fill="none" :stroke="stroke" stroke-width="4" stroke-linecap="square" class="gauge-value" />

      <!-- ticks -->
      <line
        v-for="(t, i) in ticks"
        :key="i"
        :x1="t.inner.x"
        :y1="t.inner.y"
        :x2="t.outer.x"
        :y2="t.outer.y"
        :stroke="t.major ? 'rgba(255,255,255,0.34)' : 'rgba(255,255,255,0.16)'"
        :stroke-width="t.major ? 1 : 0.6"
      />

      <!-- needle -->
      <line :x1="cx" :y1="cy" :x2="needle.x" :y2="needle.y" :stroke="stroke" stroke-width="1.25" stroke-linecap="square" class="gauge-needle" />
      <circle :cx="cx" :cy="cy" r="2" :fill="stroke" />
      <circle :cx="cx" :cy="cy" r="0.8" fill="#000" />

      <!-- readout -->
      <text x="50" y="74" text-anchor="middle" class="gauge-readout" :fill="stroke">{{ display }}{{ unit }}</text>
    </svg>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';

type Tone = 'orange' | 'cyan' | 'green' | 'red' | 'magenta';

const TONES: Record<Tone, string> = {
  orange: '#ff9900',
  cyan: '#00f6ff',
  green: '#00ff00',
  red: '#ff2b1d',
  magenta: '#ff00ff',
};

// Geometry matches nerv-ui's Gauge (MIT, github.com/mdrbx/nerv-ui).
const CX = 50;
const CY = 44;
const R = 31;
const START = 160;
const SWEEP = 220;

export default defineComponent({
  name: 'NervGauge',
  props: {
    value: { type: Number, required: true },
    min: { type: Number, default: 0 },
    max: { type: Number, default: 100 },
    label: { type: String, default: '' },
    unit: { type: String, default: '%' },
    tone: { type: String as PropType<Tone>, default: 'orange' },
    size: { type: Number, default: 150 },
    threshold: { type: Number, default: undefined },
    decimals: { type: Number, default: 0 },
  },
  computed: {
    cx: () => CX,
    cy: () => CY,
    pct(): number {
      const range = Math.max(1, this.max - this.min);
      return Math.max(0, Math.min(1, (this.value - this.min) / range));
    },
    overThreshold(): boolean {
      return this.threshold !== undefined && this.value > this.threshold;
    },
    stroke(): string {
      return this.overThreshold ? TONES.red : TONES[this.tone] || TONES.orange;
    },
    display(): string {
      return this.value.toFixed(this.decimals);
    },
    ariaLabel(): string {
      return `${this.label || 'gauge'}: ${this.display}${this.unit}`;
    },
    bgArc(): string {
      return this.arc(START, START + SWEEP);
    },
    thresholdArc(): string | null {
      if (this.threshold === undefined) return null;
      const range = Math.max(1, this.max - this.min);
      const tPct = Math.max(0, Math.min(1, (this.threshold - this.min) / range));
      const from = START + tPct * SWEEP;
      const to = START + SWEEP;
      return to - from < 0.2 ? null : this.arc(from, to);
    },
    valueArc(): string {
      const end = START + this.pct * SWEEP;
      return this.arc(START, Math.max(START + 0.1, end));
    },
    needle(): { x: number; y: number } {
      return this.point(START + this.pct * SWEEP);
    },
    ticks(): Array<{ inner: { x: number; y: number }; outer: { x: number; y: number }; major: boolean }> {
      return Array.from({ length: 13 }, (_, i) => {
        const angle = START + (i / 12) * SWEEP;
        const major = i % 3 === 0;
        const innerR = major ? R - 6 : R - 3;
        const rad = (angle * Math.PI) / 180;
        return {
          outer: this.point(angle),
          inner: { x: CX + innerR * Math.cos(rad), y: CY + innerR * Math.sin(rad) },
          major,
        };
      });
    },
  },
  methods: {
    point(deg: number): { x: number; y: number } {
      const rad = (deg * Math.PI) / 180;
      return { x: CX + R * Math.cos(rad), y: CY + R * Math.sin(rad) };
    },
    arc(fromDeg: number, toDeg: number): string {
      const a = this.point(fromDeg);
      const b = this.point(toDeg);
      const large = toDeg - fromDeg > 180 ? 1 : 0;
      return `M ${a.x} ${a.y} A ${R} ${R} 0 ${large} 1 ${b.x} ${b.y}`;
    },
  },
});
</script>

<style scoped>
.gauge {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  font-family: var(--font-nerv-mono);
}

.gauge-label {
  margin-block-end: 0.25rem;
  padding-block-end: 0.25rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.25);
  color: var(--stroke);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
}

.gauge-svg {
  overflow: visible;
  filter: drop-shadow(0 0 10px color-mix(in srgb, var(--stroke) 33%, transparent));
}

.gauge-value,
.gauge-needle {
  transition: d 0.5s linear, x2 0.5s linear, y2 0.5s linear;
}

.gauge-readout {
  font-family: var(--font-nerv-mono);
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.05em;
}
</style>
